/*
 * Region pop-up (sections/cc-region-popup.liquid).
 *
 * Shows once per browser to visitors whose country has a local partner: Australia and New
 * Zealand -> the AU partner, Canada -> the CA partner. The country comes from Shopify's
 * /browsing_context_suggestions.json (IP geolocation, works without Markets); if that request
 * fails, the browser time zone is used instead. Everyone else never sees the dialog.
 *
 * Dismissing (close, "I'm in the United States", Esc, backdrop) or following the partner link
 * stores `cc-region-gate` in localStorage so it is not shown again.
 * QA: ?cc_region=AU|NZ|CA forces a panel and ignores the stored dismissal.
 */
(() => {
  if (customElements.get('cc-region-popup')) return;

  const STORAGE_KEY = 'cc-region-gate';
  const COUNTRY_CACHE_KEY = 'cc-region-country';
  const COUNTRY_NAMES = { AU: 'Australia', NZ: 'New Zealand', CA: 'Canada' };
  const PARTNER_FOR = { AU: 'AU', NZ: 'AU', CA: 'CA' };
  const CA_ZONES = [
    'America/Toronto', 'America/Vancouver', 'America/Edmonton', 'America/Winnipeg', 'America/Halifax',
    'America/St_Johns', 'America/Regina', 'America/Whitehorse', 'America/Yellowknife', 'America/Iqaluit',
    'America/Moncton', 'America/Glace_Bay', 'America/Goose_Bay', 'America/Blanc-Sablon',
    'America/Cambridge_Bay', 'America/Creston', 'America/Dawson', 'America/Dawson_Creek',
    'America/Fort_Nelson', 'America/Inuvik', 'America/Rankin_Inlet', 'America/Resolute',
    'America/Swift_Current', 'America/Nipigon', 'America/Thunder_Bay', 'America/Atikokan',
    'America/Rainy_River', 'America/Pangnirtung', 'America/Coral_Harbour', 'Canada/Eastern',
    'Canada/Pacific', 'Canada/Mountain', 'Canada/Central', 'Canada/Atlantic', 'Canada/Newfoundland',
  ];

  const storage = {
    get(area, key) {
      try { return window[area].getItem(key); } catch (e) { return null; }
    },
    set(area, key, value) {
      try { window[area].setItem(key, value); } catch (e) { /* private mode: show again next visit */ }
    },
  };

  function countryFromTimeZone() {
    let zone = '';
    try { zone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { zone = ''; }
    if (zone.indexOf('Australia/') === 0 || zone === 'Antarctica/Macquarie') return 'AU';
    if (zone === 'Pacific/Auckland' || zone === 'Pacific/Chatham' || zone === 'NZ') return 'NZ';
    if (CA_ZONES.indexOf(zone) !== -1) return 'CA';
    return '';
  }

  async function detectCountry() {
    const cached = storage.get('sessionStorage', COUNTRY_CACHE_KEY);
    if (cached !== null) return cached;
    let country = '';
    try {
      const response = await fetch('/browsing_context_suggestions.json?country[enabled]=true', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const data = await response.json();
      country = (data && data.detected_values && data.detected_values.country && data.detected_values.country.handle) || '';
    } catch (e) {
      country = countryFromTimeZone();
    }
    country = String(country).toUpperCase();
    storage.set('sessionStorage', COUNTRY_CACHE_KEY, country);
    return country;
  }

  class CcRegionPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      try {
        this.partners = JSON.parse(this.querySelector('[data-cc-region-partners]').textContent);
      } catch (e) {
        return;
      }
      this.querySelectorAll('[data-cc-region-stay]').forEach((button) => {
        button.addEventListener('click', () => this.dismiss('us'));
      });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.dismiss('us');
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dismiss('us');
      });
      this.querySelectorAll('[data-cc-region-partner], [data-cc-region-other]').forEach((link) => {
        link.addEventListener('click', () => storage.set('localStorage', STORAGE_KEY, 'partner'));
      });
      this.start();
    }

    async start() {
      const preview = this.dataset.preview;
      if (preview && preview !== 'none') return this.open(preview);

      const forced = new URLSearchParams(window.location.search).get('cc_region');
      if (forced && PARTNER_FOR[forced.toUpperCase()]) return this.open(forced.toUpperCase());

      if (storage.get('localStorage', STORAGE_KEY)) return;
      const country = await detectCountry();
      if (PARTNER_FOR[country]) this.open(country);
    }

    open(country) {
      const partnerKey = PARTNER_FOR[country];
      const partner = this.partners[partnerKey];
      const otherKey = partnerKey === 'AU' ? 'CA' : 'AU';
      const other = this.partners[otherKey];
      if (!partner || !/^https:\/\//.test(partner.url || '')) return;
      const place = COUNTRY_NAMES[country];

      this.fill('[data-cc-region-eyebrow]', 'Shipping to ' + place);
      this.fill('[data-cc-region-title]', 'We don’t ship to ' + place);
      this.fill('[data-cc-region-text]',
        'Orders on this site are fulfilled in the United States. ' + partner.name +
        ' carries the Calm Caterpillar range in ' + place + ', with local shipping and support.');
      this.fill('[data-cc-region-partner-name]', partner.name);
      this.fill('[data-cc-region-partner-serves]', partner.serves);
      this.querySelector('[data-cc-region-partner]').href = partner.url;

      const otherLink = this.querySelector('[data-cc-region-other]');
      const elsewhere = otherLink.closest('.cc-region__elsewhere');
      if (other && /^https:\/\//.test(other.url || '')) {
        otherLink.href = other.url;
        otherLink.textContent = other.name + ' in ' + other.serves;
        elsewhere.hidden = false;
      } else {
        elsewhere.hidden = true;
      }

      if (!this.dialog.open) {
        this.dialog.showModal();
        // Start on the dialog itself (as in the design) so the close button does not open
        // with a focus ring; Tab still moves straight into the controls.
        this.dialog.focus({ preventScroll: true });
      }
    }

    fill(selector, text) {
      const node = this.querySelector(selector);
      if (node) node.textContent = text;
    }

    dismiss(choice) {
      if (this.dataset.preview === 'none' || !this.dataset.preview) {
        storage.set('localStorage', STORAGE_KEY, choice);
      }
      this.dialog.close();
    }
  }

  customElements.define('cc-region-popup', CcRegionPopup);
})();
